import { FieldDefinition, UsageModel } from "./fieldDefinition";
import {
  FieldHydrationContext,
  FieldModelType,
  FieldProposal,
  FieldRange,
  FieldUser,
} from "./types";

type FieldValueResolverArgsBase = {
  fieldDefinition: FieldDefinition;
  context: FieldHydrationContext;
};

type UserFieldArgs = FieldValueResolverArgsBase & {
  modelInstance: FieldUser;
};

type ProposalFieldArgs = FieldValueResolverArgsBase & {
  modelInstance: FieldProposal;
};

type RangeFieldArgs = FieldValueResolverArgsBase & {
  modelInstance: FieldRange;
};

type FieldValueResolverArgs = UserFieldArgs | ProposalFieldArgs | RangeFieldArgs;

export class FieldValueResolver {
  fieldDefinition: FieldDefinition;
  modelInstance: FieldModelType;
  context: FieldHydrationContext;

  constructor(args: FieldValueResolverArgs) {
    this.fieldDefinition = args.fieldDefinition;
    this.modelInstance = args.modelInstance;
    this.context = args.context;
  }

  static getUsageModel(modelInstance: FieldModelType): UsageModel {
    return modelInstance?.explicitKind as UsageModel;
  }

  get usageModel(): UsageModel {
    return FieldValueResolver.getUsageModel(this.modelInstance);
  }

  private typedModelInstance<T extends FieldModelType>(): T {
    return this.modelInstance as T;
  }

  static validateAsUser(modelInstance: FieldModelType): modelInstance is FieldUser {
    return FieldValueResolver.getUsageModel(modelInstance) === UsageModel.User;
  }

  static validateAsProposal(modelInstance: FieldModelType): modelInstance is FieldProposal {
    return FieldValueResolver.getUsageModel(modelInstance) === UsageModel.Proposal;
  }

  static validateAsRange(modelInstance: FieldModelType): modelInstance is FieldRange {
    return FieldValueResolver.getUsageModel(modelInstance) === UsageModel.Range;
  }

  getValue() {
    switch (this.usageModel) {
      case UsageModel.Proposal:
        return this.getValueFromProposal();
      case UsageModel.User:
        return this.getValueFromUser();
      case UsageModel.Range:
        return this.getValueFromRange();
      default:
        throw new Error("Invalid BaseModel", this.usageModel);
    }
  }

  getFormattedValue() {
    const value = this.getValue();

    return this.fieldDefinition.formatter
      ? this.fieldDefinition.formatter({
          value,
          instance: this.modelInstance,
          context: this.context,
        })
      : value;
  }

  private getValueFromProposal(): any {
    const proposal = this.typedModelInstance<FieldProposal>();
    switch (this.fieldDefinition.baseModel) {
      case UsageModel.Proposal:
        return this.fieldDefinition.valueGetter(proposal, this.context, this.modelInstance);
      case UsageModel.User:
        return this.fieldDefinition.valueGetter(proposal?.owner, this.context, this.modelInstance);
      case UsageModel.Range:
        return this.fieldDefinition.valueGetter(
          proposal?.range ?? proposal?.owner?.range,
          this.context,
          this.modelInstance,
        );
      default:
        throw new Error(`Cannot get ${this.fieldDefinition.baseModel} field from proposal model`);
    }
  }

  private getValueFromUser(): any {
    const user = this.typedModelInstance<FieldUser>();

    switch (this.fieldDefinition.baseModel) {
      case UsageModel.User:
        return this.fieldDefinition.valueGetter(user, this.context, this.modelInstance);
      case UsageModel.Range:
        return this.fieldDefinition.valueGetter(user.range, this.context, this.modelInstance);
      default:
        throw new Error(`Cannot get ${this.fieldDefinition.baseModel} field from user model`);
    }
  }

  private getValueFromRange(): any {
    const range = this.typedModelInstance<FieldRange>();
    if (this.fieldDefinition.baseModel === UsageModel.Range) {
      return this.fieldDefinition.valueGetter(range, this.context, this.modelInstance);
    }

    throw new Error(`Cannot get ${this.fieldDefinition.baseModel} field from range model`);
  }
}
